// Document Upload Utilities for WEQASA
// Handles Cloudinary uploads with validation

import axios from 'axios';

// Document type configurations
export const DOCUMENT_CONFIGS = {
  identity_document: {
    label: 'Documento di Identità',
    acceptedFormats: '.jpg,.jpeg,.png,.pdf',
    maxSizeMB: 10,
    required: true,
    helpText: 'Carica una foto chiara della tua carta d\'identità o passaporto'
  },
  photos: {
    label: 'Foto Immobile',
    acceptedFormats: '.jpg,.jpeg,.png',
    maxSizeMB: 20,
    required: true,
    multiple: true,
    minCount: 15,
    helpText: 'Minimo 15 foto (almeno una per ambiente). Max 20 MB per foto. Risoluzione consigliata: 3000px o superiore'
  },
  planimetria_catastale: {
    label: 'Planimetria Catastale',
    acceptedFormats: '.pdf',
    maxSizeMB: 20,
    required: true,
    helpText: 'Solo formato PDF'
  },
  visura_catastale: {
    label: 'Visura Catastale',
    acceptedFormats: '.pdf',
    maxSizeMB: 20,
    required: true,
    helpText: 'Solo formato PDF'
  },
  atto_provenienza: {
    label: 'Atto di Provenienza',
    acceptedFormats: '.pdf',
    maxSizeMB: 20,
    required: true,
    helpText: 'Atto di compravendita, donazione o successione. Solo PDF'
  },
  certificato_ape: {
    label: 'Certificato APE',
    acceptedFormats: '.pdf',
    maxSizeMB: 10,
    required: false,
    helpText: 'Attestato Prestazione Energetica (opzionale)'
  },
  urbanistica: {
    label: 'Documentazione Urbanistica',
    acceptedFormats: '.pdf',
    maxSizeMB: 20,
    required: false,
    helpText: 'Permessi, condoni, sanatorie (se applicabili)'
  }
};

// Validation error messages in Italian
export const ERROR_MESSAGES = {
  invalidFormat: 'Formato non valido. ',
  fileTooLarge: 'File troppo grande. Dimensione massima: ',
  invalidContent: 'Il file caricato non sembra essere un documento valido.',
  invalidIdentity: 'Il file caricato non sembra essere un documento di identità valido.',
  pdfOnly: 'Questo documento deve essere caricato in formato PDF.',
  uploadFailed: 'Errore nel caricamento. Riprova.',
  invalidCharacters: 'Il campo contiene caratteri non consentiti.',
  requiredField: 'Campo obbligatorio.'
};

// Validate file before upload
export const validateFile = async (file, documentType) => {
  const config = DOCUMENT_CONFIGS[documentType];
  if (!config) return { valid: false, error: 'Tipo documento non riconosciuto' };

  // Check file extension
  const ext = '.' + file.name.split('.').pop().toLowerCase();
  const acceptedExts = config.acceptedFormats.split(',');
  
  if (!acceptedExts.includes(ext)) {
    if (config.acceptedFormats === '.pdf') {
      return { valid: false, error: ERROR_MESSAGES.pdfOnly };
    }
    return { 
      valid: false, 
      error: `${ERROR_MESSAGES.invalidFormat}Formati accettati: ${config.acceptedFormats}` 
    };
  }

  // Check file size
  const maxSizeBytes = config.maxSizeMB * 1024 * 1024;
  if (file.size > maxSizeBytes) {
    return { 
      valid: false, 
      error: `${ERROR_MESSAGES.fileTooLarge}${config.maxSizeMB} MB` 
    };
  }

  // Backend validation for additional checks
  try {
    const response = await axios.post('/upload/validate', {
      filename: file.name,
      file_size: file.size,
      mime_type: file.type,
      document_type: documentType
    });
    
    if (!response.data.valid) {
      return { valid: false, error: response.data.error_message };
    }
  } catch (error) {
    // If backend validation fails, proceed with client-side validation only
    console.warn('Backend validation unavailable:', error);
  }

  return { valid: true };
};

// Upload file to Cloudinary (when configured)
export const uploadToCloud = async (file, folder, documentType, onProgress) => {
  try {
    // Get signature from backend
    const sigResponse = await axios.get('/upload/signature', {
      params: { folder, resource_type: 'auto', document_type: documentType }
    });
    
    const { signature, timestamp, cloud_name, api_key, upload_url } = sigResponse.data;

    // Create form data
    const formData = new FormData();
    formData.append('file', file);
    formData.append('api_key', api_key);
    formData.append('timestamp', timestamp);
    formData.append('signature', signature);
    formData.append('folder', folder);

    // Upload to Cloudinary
    const uploadResponse = await axios.post(upload_url, formData, {
      onUploadProgress: (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        if (onProgress) onProgress(percentCompleted);
      }
    });

    return {
      success: true,
      url: uploadResponse.data.secure_url,
      public_id: uploadResponse.data.public_id
    };
  } catch (error) {
    // If Cloudinary is not configured (503) or any upload service error, use simulated upload
    const status = error.response?.status;
    if (status === 503 || status === 500 || !error.response) {
      // Simulate upload with delay and create a local preview URL
      return new Promise((resolve) => {
        let progress = 0;
        const interval = setInterval(() => {
          progress += 20;
          if (onProgress) onProgress(progress);
          if (progress >= 100) {
            clearInterval(interval);
            
            // Create a data URL for local preview (for images)
            const reader = new FileReader();
            reader.onloadend = () => {
              const timestamp = Date.now();
              resolve({
                success: true,
                // Use data URL for images so they can be previewed
                url: reader.result,
                public_id: `${folder}/${timestamp}_${file.name}`,
                simulated: true,
                filename: file.name
              });
            };
            reader.onerror = () => {
              // Fallback for non-image files
              const timestamp = Date.now();
              resolve({
                success: true,
                url: `local://${folder}/${timestamp}_${file.name}`,
                public_id: `${folder}/${timestamp}_${file.name}`,
                simulated: true,
                filename: file.name
              });
            };
            reader.readAsDataURL(file);
          }
        }, 200);
      });
    }
    
    return {
      success: false,
      error: error.response?.data?.detail || ERROR_MESSAGES.uploadFailed
    };
  }
};

// Validate property field
export const validatePropertyField = (fieldName, value) => {
  if (!value || !value.trim()) {
    return { valid: false, error: ERROR_MESSAGES.requiredField };
  }

  const trimmedValue = value.trim();

  switch (fieldName) {
    case 'address':
      if (trimmedValue.length < 5) {
        return { valid: false, error: 'L\'indirizzo deve contenere almeno 5 caratteri.' };
      }
      if (!/^[a-zA-Z0-9àèéìòù\s,.\-'\/]+$/i.test(trimmedValue)) {
        return { valid: false, error: 'Formato non valido. Utilizzare solo caratteri alfanumerici.' };
      }
      break;

    case 'postal_code':
      if (!/^\d{5}$/.test(trimmedValue)) {
        return { valid: false, error: 'Il CAP deve essere composto da 5 cifre.' };
      }
      break;

    case 'province':
      if (!/^[A-Z]{2}$/i.test(trimmedValue)) {
        return { valid: false, error: 'La provincia deve essere indicata con 2 lettere (es. RM, MI).' };
      }
      break;

    case 'foglio':
    case 'particella':
    case 'subalterno':
      if (!/^[a-zA-Z0-9\-\/]+$/.test(trimmedValue)) {
        return { valid: false, error: ERROR_MESSAGES.invalidCharacters };
      }
      break;

    case 'sqm':
    case 'rooms':
    case 'floor':
    case 'bathrooms':
      const num = parseInt(trimmedValue);
      if (isNaN(num) || num < 0) {
        return { valid: false, error: 'Inserire un valore numerico positivo.' };
      }
      break;
  }

  return { valid: true };
};

// Calculate document completion percentage
export const calculateCompletion = (documents) => {
  const requiredDocs = Object.entries(DOCUMENT_CONFIGS)
    .filter(([_, config]) => config.required)
    .map(([key]) => key);
  
  let uploaded = 0;
  const missing = [];

  requiredDocs.forEach(docKey => {
    const doc = documents[docKey];
    if (doc) {
      if (Array.isArray(doc) && doc.length > 0) {
        uploaded++;
      } else if (typeof doc === 'string' && doc) {
        uploaded++;
      } else if (typeof doc === 'object' && doc.url) {
        uploaded++;
      } else {
        missing.push(docKey);
      }
    } else {
      missing.push(docKey);
    }
  });

  const percentage = Math.round((uploaded / requiredDocs.length) * 100);

  return {
    total: requiredDocs.length,
    uploaded,
    missing: missing.map(key => DOCUMENT_CONFIGS[key]?.label || key),
    percentage
  };
};
